import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { CategoryType } from '../../games/constants/types';
import { NavigationPod } from '../../NavigationPod/NavigationPod';
import { SearchContainer } from '../../search/SearchContainer';

type CasinoBrowseProps = Omit<ComponentProps<'div'>, 'children'> & {
  vertical: 'casino' | 'live-casino';
  namespace: string[];
  categories: CategoryType[];
};

/**
 * Re-usable component to render browse page for casino or live casino.
 * @param vertical - The vertical of the browse page (casino or live-casino)
 * @param namespace - Array of namespaces (first should be page specific namespace, followed by categories namespace)
 * @param categories - Categories config to render on the page
 * @returns A React component that renders the browse page for casino or live casino
 */
function CasinoBrowse({ vertical, namespace, categories, className, ...props }: CasinoBrowseProps) {
  const [browseNamespace, categoriesNamespace] = namespace;
  const t = useTranslations(browseNamespace);
  const t2 = useTranslations(categoriesNamespace);

  const hrefResolver = (slug: string) => (vertical === 'live-casino' ? ROUTES.LIVE_CASINO.CATEGORY(slug) : ROUTES.CASINO.CATEGORY(slug));

  return (
    <div className={twMerge('pb-10 pt-6', className)} data-track-section="Browse" {...props}>
      <SearchContainer placeholder={t('search_placeholder')} searchType="games" vertical={vertical}>
        <div className="ll-container-inner">
          <p className="mt-3 mb-6 paragraph-strong">{t('title', { count: categories.length })}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="categories-grid" data-track-container="Categories">
            {categories.map(({ id, slug, icon, trackName }) => (
              <NavigationPod
                key={id}
                href={hrefResolver(slug)}
                renderLabel={(_, label) => <h2 className="paragraph-explanatory-strong">{label}</h2>}
                className="flex-col"
                icon={icon}
                label={t2(id)}
                data-testid={`category-${slug}`}
                data-track-name={trackName}
              />
            ))}
          </div>
        </div>
      </SearchContainer>
    </div>
  );
}

export { CasinoBrowse };
